import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  ClipboardCheck, 
  Search, 
  Camera, 
  MapPin, 
  Clock, 
  User,
  Car,
  AlertTriangle,
  Fuel,
  Gauge,
  ChevronLeft,
  ChevronRight,
  X,
  Loader2,
  FileText,
  CheckCircle,
  Image as ImageIcon,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import ManualDamageReporter from '@/components/inspections/ManualDamageReporter';
import InspectionSignatureSection from '@/components/inspections/InspectionSignatureSection';
import InspectionDriverSelfieSection from '@/components/inspections/InspectionDriverSelfieSection';
import {
  isInspectionVehicleDetected,
  getInspectionVehicleValidationMessage,
  normalizeMediaDataUrl,
  normalizeInspectionPhotos,
  getInspectionPhotoCount,
} from '@/utils/inspectionDisplay';
import InspectionLocationRow from '@/components/inspections/InspectionLocationRow';

function getAdminBackendBaseUrl() {
  const env = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/+$/, '');
  if (env) return env;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return String(window.location.origin).replace(/\/+$/, '');
  }
  return '';
}

/** Driver app stores flat ai_analysis; older web used ai_analysis.analysis or ai_summary. */
const getAiCore = (inspection) => {
  const ai = inspection?.ai_analysis;
  if (!ai || typeof ai !== 'object') return null;
  if (ai.analysis && typeof ai.analysis === 'object') return ai.analysis;
  return ai;
};

const getInspectionDamageCount = (inspection) => {
  if (Array.isArray(inspection?.ai_summary?.damages_detected)) {
    return inspection.ai_summary.damages_detected.length;
  }
  if (Array.isArray(inspection?.detected_damages)) {
    return inspection.detected_damages.length;
  }
  const core = getAiCore(inspection);
  if (Array.isArray(core?.damages)) return core.damages.length;
  return 0;
};

const getInspectionOdometer = (inspection) =>
  inspection?.ai_summary?.odometer_reading ||
  inspection?.odometer_reading ||
  getAiCore(inspection)?.odometer ||
  null;

const getInspectionFuelLevel = (inspection) =>
  inspection?.ai_summary?.fuel_level ||
  inspection?.fuel_level ||
  getAiCore(inspection)?.fuel_level ||
  null;

const InspectionsTab = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [loadingInspectionId, setLoadingInspectionId] = useState(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const detailFetchSeq = useRef(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [filterType, setFilterType] = useState('all');
  const [fetchError, setFetchError] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [aiStatusLoading, setAiStatusLoading] = useState(true);

  const fetchAiStatus = useCallback(async () => {
    const base = getAdminBackendBaseUrl();
    if (!base) {
      setAiStatus(null);
      setAiStatusLoading(false);
      return;
    }
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${base}/api/admin/inspection-ai/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setAiStatus(await response.json());
      } else {
        setAiStatus(null);
      }
    } catch (error) {
      console.error('Inspection AI status check failed:', error);
      setAiStatus(null);
    } finally {
      setAiStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAiStatus();
  }, [fetchAiStatus]);

  const fetchInspections = useCallback(async () => {
    const base = getAdminBackendBaseUrl();
    if (!base) {
      setFetchError('missing_backend_url');
      setInspections([]);
      setLoading(false);
      return;
    }
    const FETCH_MS = 240000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_MS);
    setLoading(true);
    try {
      setFetchError(null);
      const token = localStorage.getItem('admin_token');
      let url = `${base}/api/admin/inspections`;
      if (filterType !== 'all') {
        url += `?inspection_type=${filterType}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
        signal: controller.signal,
      });
      
      if (response.ok) {
        const data = await response.json();
        setInspections(data.inspections || []);
      } else {
        setFetchError(`http_${response.status}`);
        setInspections([]);
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
      if (error?.name === 'AbortError') {
        setFetchError('timeout');
      } else {
        setFetchError('network');
      }
      setInspections([]);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchInspections();
  }, [fetchInspections]);

  const runInspectionAiAnalysis = async () => {
    if (!selectedInspection?.id) return;
    setAiAnalyzing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const base = getAdminBackendBaseUrl();
      const response = await fetch(
        `${base}/api/admin/inspections/${selectedInspection.id}/analyze`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${response.status}`);
      }
      const data = await response.json();
      setSelectedInspection(data);
      fetchAiStatus();
      toast.success(t('inspections_ai_analyze_success', { defaultValue: 'Análisis IA completado' }));
    } catch (error) {
      console.error('Inspection AI analyze failed:', error);
      toast.error(
        t('inspections_ai_analyze_failed', {
          defaultValue: 'No se pudo analizar las fotos con IA',
        }) + (error?.message ? `: ${error.message}` : ''),
      );
    } finally {
      setAiAnalyzing(false);
    }
  };

  const closeDetailModal = () => {
    setModalOpen(false);
    setDetailLoading(false);
    setDetailError(null);
    setLoadingInspectionId(null);
    setSelectedInspection(null);
  };

  const fetchInspectionDetail = async (inspectionId) => {
    if (loadingInspectionId === inspectionId && detailLoading) return;
    const token = localStorage.getItem('admin_token');
    const base = getAdminBackendBaseUrl();
    if (!base) {
      setDetailError('missing_backend');
      setModalOpen(true);
      return;
    }

    const seq = ++detailFetchSeq.current;
    setLoadingInspectionId(inspectionId);
    setDetailLoading(true);
    setDetailError(null);
    setSelectedInspection(null);
    setModalOpen(true);

    try {
      const response = await fetch(`${base}/api/admin/inspections/${inspectionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (seq !== detailFetchSeq.current) return;

      if (!response.ok) {
        setDetailError(response.status === 404 ? 'not_found' : `http_${response.status}`);
        return;
      }

      const data = await response.json();
      setSelectedInspection(data);
      setCurrentPhotoIndex(0);
      setDetailError(null);
    } catch (error) {
      if (seq !== detailFetchSeq.current) return;
      console.error('Error fetching inspection detail:', error);
      setDetailError('network');
      toast.error(
        t('inspections_detail_error', {
          defaultValue: 'No se pudo cargar el peritaje. Inténtelo de nuevo.',
        }),
      );
    } finally {
      if (seq === detailFetchSeq.current) {
        setDetailLoading(false);
        setLoadingInspectionId(null);
      }
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const searchLower = searchTerm.toLowerCase();
    const plate =
      inspection.vehicle_plate ||
      inspection.service_info?.vehicle_info?.plate ||
      '';
    const brand = inspection.service_info?.vehicle_info?.brand || '';
    const model = inspection.service_info?.vehicle_info?.model || '';
    return (
      plate.toLowerCase().includes(searchLower) ||
      inspection.driver_info?.name?.toLowerCase().includes(searchLower) ||
      brand.toLowerCase().includes(searchLower) ||
      model.toLowerCase().includes(searchLower)
    );
  });

  const formatDate = (isoString) => {
    if (!isoString) return t('gps_live_na');
    const date = new Date(isoString);
    return date.toLocaleDateString(dateLocale, { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInspectionTypeBadge = (type) => {
    if (type === 'pickup') {
      return <Badge className="bg-blue-100 text-blue-700">{t('pickup')}</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">{t('delivery')}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">{t('inspections_loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inspections-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardCheck className="w-6 h-6 text-purple-600" />
            {t('inspections_title')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('inspections_subtitle')}
          </p>
        </div>
      </div>

      {!aiStatusLoading && aiStatus && (
        (aiStatus.model_deprecated ||
          aiStatus.health_ok === false ||
          aiStatus.provider_ready === false ||
          aiStatus.runtime_fallback) && (
          <Alert
            variant={aiStatus.health_ok === false ? 'destructive' : 'default'}
            className={
              aiStatus.health_ok === false
                ? 'border-red-300 bg-red-50'
                : 'border-amber-300 bg-amber-50'
            }
          >
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t('inspections_ai_status_alert_title')}</AlertTitle>
            <AlertDescription className="space-y-1 text-sm">
              {aiStatus.model_deprecated && (
                <p>
                  {t('inspections_ai_model_deprecated', {
                    configured: aiStatus.configured_model || '—',
                    effective: aiStatus.effective_model,
                    recommended: aiStatus.recommended_model,
                  })}
                </p>
              )}
              {aiStatus.runtime_fallback && (
                <p>
                  {t('inspections_ai_runtime_fallback', {
                    from: aiStatus.runtime_fallback.from_model,
                    to: aiStatus.runtime_fallback.to_model,
                  })}
                </p>
              )}
              {aiStatus.provider_ready === false && (
                <p>{t('inspections_ai_provider_not_ready')}</p>
              )}
              {aiStatus.health_ok === false && aiStatus.health_error && (
                <p>{aiStatus.health_error}</p>
              )}
              {aiStatus.warning && <p>{aiStatus.warning}</p>}
            </AlertDescription>
          </Alert>
        )
      )}

      {/* Filters */}
      {fetchError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-800">
            {fetchError === 'missing_backend_url'
              ? 'REACT_APP_BACKEND_URL no está definido: no se puede cargar la API de peritajes. Configure la URL del backend en el build o use el mismo origen (proxy /api).'
              : fetchError === 'network'
                ? 'No se pudo conectar con el servidor de peritajes. Compruebe la red y que la API esté en el mismo origen o en REACT_APP_BACKEND_URL.'
              : fetchError === 'timeout'
                ? t('inspections_fetch_timeout')
                : `Error al cargar peritajes (${fetchError}). Compruebe que el backend expone GET /api/admin/inspections y que el token de admin es válido.`}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t('inspections_search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant={filterType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('all')}
          >
            {t('admin_all')}
          </Button>
          <Button 
            variant={filterType === 'pickup' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('pickup')}
          >
            {t('pickup')}
          </Button>
          <Button 
            variant={filterType === 'delivery' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterType('delivery')}
          >
            {t('delivery')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{inspections.length}</p>
                <p className="text-sm text-gray-500">{t('inspections_stat_total')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Camera className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inspections.reduce((acc, i) => acc + getInspectionPhotoCount(i), 0)}
                </p>
                <p className="text-sm text-gray-500">{t('inspections_stat_photos')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inspections.filter(i => getInspectionDamageCount(i) > 0).length}
                </p>
                <p className="text-sm text-gray-500">{t('inspections_stat_with_damage')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {inspections.filter(i => getInspectionDamageCount(i) === 0).length}
                </p>
                <p className="text-sm text-gray-500">{t('inspections_stat_no_damage')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Inspections List */}
      {filteredInspections.length > 0 ? (
        <div className="grid gap-4">
          {filteredInspections.map((inspection) => (
            (() => {
              const normalizedPhotos = normalizeInspectionPhotos(inspection);
              const photoCount = getInspectionPhotoCount(inspection);
              const damageCount = getInspectionDamageCount(inspection);
              const odometer = getInspectionOdometer(inspection);
              const fuelLevel = getInspectionFuelLevel(inspection);
              const vehiclePlate =
                inspection.vehicle_plate ||
                inspection.service_info?.vehicle_info?.plate ||
                t('gps_live_na');
              return (
            <Card
              key={inspection.id}
              className={`cursor-pointer hover:shadow-md transition-shadow ${loadingInspectionId === inspection.id ? 'opacity-80' : ''}`}
              onClick={() => fetchInspectionDetail(inspection.id)}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Vehicle Info */}
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      {normalizedPhotos[0]?.url ? (
                        <>
                          <img 
                            src={normalizedPhotos[0].url} 
                            alt="Preview"
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          {normalizedPhotos.length > 1 && (
                            <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                              +{normalizedPhotos.length - 1}
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-gray-400" />
                          </div>
                          {photoCount > 0 && (
                            <span className="absolute -bottom-1 -right-1 bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                              {photoCount}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-500" />
                        <span className="font-bold text-lg">{vehiclePlate}</span>
                        {getInspectionTypeBadge(inspection.inspection_type)}
                      </div>
                      {inspection.service_info?.vehicle_info && (
                        <p className="text-gray-600 text-sm">
                          {inspection.service_info.vehicle_info.brand} {inspection.service_info.vehicle_info.model}
                        </p>
                      )}
                      {inspection.driver_info && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                          <User className="w-3 h-3" />
                          {inspection.driver_info.name}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* AI Summary */}
                  <div className="flex flex-wrap gap-2">
                    {damageCount > 0 ? (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        {t('inspections_damage_count', { count: damageCount })}
                      </Badge>
                    ) : (
                      <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        {t('inspections_no_damage_short')}
                      </Badge>
                    )}
                    
                    {inspection.has_driver_selfie && (
                      <Badge variant="outline" className="flex items-center gap-1 border-blue-200 text-blue-700">
                        <User className="w-3 h-3" />
                        {t('inspections_has_driver_selfie_badge')}
                      </Badge>
                    )}

                    {odometer && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Gauge className="w-3 h-3" />
                        {odometer} km
                      </Badge>
                    )}
                    
                    {fuelLevel && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Fuel className="w-3 h-3" />
                        {fuelLevel}
                      </Badge>
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    disabled={loadingInspectionId === inspection.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchInspectionDetail(inspection.id);
                    }}
                  >
                    {loadingInspectionId === inspection.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    ) : (
                      t('inspections_view_btn', { defaultValue: 'Ver peritaje' })
                    )}
                  </Button>

                  {/* Date & Location */}
                  <div className="text-sm text-gray-500 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <Clock className="w-3 h-3" />
                      {formatDate(inspection.created_at)}
                    </div>
                    {(inspection.location?.address ||
                      inspection.service_info?.delivery_location?.address ||
                      inspection.service_info?.pickup_location?.address) && (
                      <div className="flex items-center gap-1 justify-end mt-1">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate max-w-[200px]">
                          {inspection.location?.address ||
                            inspection.service_info?.delivery_location?.address ||
                            inspection.service_info?.pickup_location?.address}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
              );
            })()
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">{t('inspections_empty_title')}</h3>
              <p className="text-gray-500 mt-1">
                {searchTerm ? t('inspections_empty_search') : t('inspections_empty_hint')}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inspection Detail Modal */}
      <Dialog
        open={modalOpen}
        onOpenChange={(open) => {
          if (!open) closeDetailModal();
          else setModalOpen(true);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {detailLoading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
              <p className="text-gray-600">
                {t('inspections_detail_loading', { defaultValue: 'Cargando peritaje…' })}
              </p>
            </div>
          )}
          {!detailLoading && detailError && (
            <div className="py-12 text-center space-y-3">
              <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />
              <p className="text-gray-700">
                {t('inspections_detail_error', {
                  defaultValue: 'No se pudo cargar el peritaje. Inténtelo de nuevo.',
                })}
              </p>
              <Button type="button" variant="outline" onClick={() => closeDetailModal()}>
                {t('admin_close', { defaultValue: 'Cerrar' })}
              </Button>
            </div>
          )}
          {!detailLoading && !detailError && selectedInspection && (
            <>
              {(() => {
                const selectedPhotos = normalizeInspectionPhotos(selectedInspection);
                const selectedPhotoCount = selectedPhotos.length;
                const selectedDamageCount = getInspectionDamageCount(selectedInspection);
                return (
              <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  {t('inspections_modal_title', { plate: selectedInspection.vehicle_plate || selectedInspection.service_info?.vehicle_info?.plate || t('gps_live_na') })}
                  {getInspectionTypeBadge(selectedInspection.inspection_type)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Photo Gallery */}
                {selectedPhotos.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      {t('inspections_photo_gallery', { count: selectedPhotos.length })}
                    </h4>
                    
                    {/* Main Photo */}
                    <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={normalizeMediaDataUrl(selectedPhotos[currentPhotoIndex]?.url)}
                        alt={t('inspections_photo_alt', { n: currentPhotoIndex + 1 })}
                        className="w-full h-80 object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                      
                      {/* Navigation Arrows */}
                      {selectedPhotos.length > 1 && (
                        <>
                          <button 
                            onClick={() => setCurrentPhotoIndex(i => i === 0 ? selectedPhotos.length - 1 : i - 1)}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                          >
                            <ChevronLeft className="w-5 h-5" />
                          </button>
                          <button 
                            onClick={() => setCurrentPhotoIndex(i => i === selectedPhotos.length - 1 ? 0 : i + 1)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 rounded-full text-white hover:bg-black/70"
                          >
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      
                      {/* Photo Type Badge */}
                      <div className="absolute bottom-2 left-2">
                        <Badge className="bg-black/70 text-white">
                          {selectedPhotos[currentPhotoIndex]?.type || t('inspections_photo_type_default')}
                        </Badge>
                      </div>
                      
                      {/* Counter */}
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                        {currentPhotoIndex + 1} / {selectedPhotos.length}
                      </div>
                    </div>

                    {/* Thumbnails */}
                    <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
                      {selectedPhotos.map((photo, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentPhotoIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                            index === currentPhotoIndex ? 'border-blue-500' : 'border-transparent'
                          }`}
                        >
                          <img
                            src={normalizeMediaDataUrl(photo.url)}
                            alt={`Thumbnail ${index + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        </button>
                      ))}
                    </div>

                    {/* AI Analysis for current photo */}
                    {selectedPhotos[currentPhotoIndex]?.ai_analysis && (
                      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>{t('inspections_ai_analysis_prefix')}</strong> {selectedPhotos[currentPhotoIndex].ai_analysis}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* AI Summary — vision analysis on inspection photos */}
                {(selectedPhotoCount > 0 || selectedInspection.ai_summary || getAiCore(selectedInspection)) && (
                  <div>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        {t('inspections_ai_summary_title')}
                      </h4>
                      {selectedPhotoCount > 0 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={runInspectionAiAnalysis}
                          disabled={aiAnalyzing}
                          data-testid="inspection-analyze-ai-btn"
                        >
                          {aiAnalyzing ? (
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4 mr-1" />
                          )}
                          {t('inspections_ai_analyze_btn', { defaultValue: 'Analizar fotos con IA' })}
                        </Button>
                      )}
                    </div>
                    {selectedInspection.ai_analysis_status === 'processing' && (
                      <p className="text-sm text-amber-700 mb-3 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t('inspections_ai_processing', { defaultValue: 'Analizando fotos con IA…' })}
                      </p>
                    )}
                    {selectedInspection.ai_analysis_status === 'failed' && (
                      <p className="text-sm text-red-600 mb-3">
                        {t('inspections_ai_failed', {
                          defaultValue:
                            'El análisis IA no está disponible. Comprueba OPENAI_API_KEY en el servidor y pulsa «Analizar fotos con IA».',
                        })}
                      </p>
                    )}
                    {!isInspectionVehicleDetected(selectedInspection) && (
                      <div className="mb-4 p-4 rounded-lg border border-amber-300 bg-amber-50">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-amber-900">{t('inspections_not_vehicle_title')}</p>
                            <p className="text-sm text-amber-800 mt-1">
                              {getInspectionVehicleValidationMessage(selectedInspection) ||
                                t('inspections_not_vehicle_message')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Damages */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle
                              className={`w-4 h-4 ${
                                !isInspectionVehicleDetected(selectedInspection)
                                  ? 'text-amber-500'
                                  : selectedDamageCount
                                    ? 'text-red-500'
                                    : 'text-green-500'
                              }`}
                            />
                            <span className="font-medium">{t('inspections_damages_detected')}</span>
                          </div>
                          {!isInspectionVehicleDetected(selectedInspection) ? (
                            <p className="text-sm text-amber-700">{t('inspections_not_vehicle_message')}</p>
                          ) : selectedDamageCount > 0 ? (
                            <ul className="list-disc list-inside text-sm text-gray-600">
                              {(selectedInspection.ai_summary?.damages_detected || []).map((damage, i) => (
                                <li key={i}>{typeof damage === 'string' ? damage : JSON.stringify(damage)}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-sm text-green-600">{t('inspections_no_damages_message')}</p>
                          )}
                        </CardContent>
                      </Card>

                      {/* Vehicle Stats */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-gray-600">
                                <Gauge className="w-4 h-4" />
                                {t('inspections_odometer')}
                              </span>
                              <span className="font-medium">
                                {!isInspectionVehicleDetected(selectedInspection)
                                  ? t('gps_live_na')
                                  : getInspectionOdometer(selectedInspection) || t('gps_live_na')}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-2 text-gray-600">
                                <Fuel className="w-4 h-4" />
                                {t('inspections_fuel')}
                              </span>
                              <span className="font-medium">
                                {!isInspectionVehicleDetected(selectedInspection)
                                  ? t('gps_live_na')
                                  : getInspectionFuelLevel(selectedInspection) || t('gps_live_na')}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-600">{t('inspections_overall_condition')}</span>
                              <Badge variant="outline">
                                {!isInspectionVehicleDetected(selectedInspection)
                                  ? t('gps_live_na')
                                  : (selectedInspection.ai_summary &&
                                      selectedInspection.ai_summary.overall_condition) ||
                                    getAiCore(selectedInspection)?.overall_condition ||
                                    t('gps_live_na')}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                )}

                {/* Manual damage reporting (admin can always add; available even when AI returned no damages). */}
                <ManualDamageReporter
                  inspection={selectedInspection}
                  role="admin"
                  onUpdated={(updated) => setSelectedInspection(updated)}
                />

                <InspectionSignatureSection
                  inspection={selectedInspection}
                  title={t('inspections_signature_title')}
                  alt={t('inspections_signature_alt')}
                />

                <InspectionDriverSelfieSection inspection={selectedInspection} />

                {/* Location & Time */}
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <InspectionLocationRow
                    inspection={selectedInspection}
                    unavailableLabel={t('inspections_location_unavailable')}
                  />
                  <div className="flex items-center gap-2 text-gray-600">
                    <Clock className="w-4 h-4" />
                    <span>{formatDate(selectedInspection.created_at)}</span>
                  </div>
                </div>

                {/* Driver Info */}
                {selectedInspection.driver_info && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-4 h-4" />
                      <span>{t('inspections_performed_by')} <strong>{selectedInspection.driver_info.name}</strong></span>
                      {selectedInspection.driver_info.phone && (
                        <span className="text-blue-600">({selectedInspection.driver_info.phone})</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              </>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InspectionsTab;
